import type { SchemaPathTree } from '@angular/forms/signals';
import { email, required } from '@angular/forms/signals';
import type { Contact, ContactForm } from '@indutrust/shared-model';
import { noWhitespaceValidator, phoneNumberValidator } from '../form';

/**
 * Returns an initial {@link ContactForm} value, optionally seeded from an
 * existing {@link Contact}.
 *
 * Replaces the previous `createContactFormGroup` factory. Used as the initial
 * value of the contact form's data signal.
 *
 * @param contact Optional partial contact used to pre-fill the form values.
 * @returns A plain `ContactForm` value with sensible defaults.
 * @example
 * const model = signal<ContactForm>(createContactFormValue());
 * const contactForm = form(model, contactFormSchema);
 */
export function createContactFormValue(
  contact?: Partial<Contact>
): ContactForm {
  return {
    type: contact?.type ?? 'admin',
    first_name: contact?.first_name ?? '',
    last_name: contact?.last_name ?? '',
    email: contact?.email ?? '',
    number: contact?.number ?? '',
    has_whatsapp: contact?.has_whatsapp ?? false,
    indutrustMainPoc: contact?.indutrustMainPoc ?? false,
    country_id: contact?.country_id ?? null,
    country_code: contact?.country_code ?? null,
  };
}

/**
 * Schema fragment applying the contact form's validation rules to a path tree
 * that exposes the {@link ContactForm} fields.
 *
 * Equivalent to the previous `createContactFormGroup` validator wiring. Bound
 * either directly (when used as a top-level form) or through `apply()` /
 * `applyEach()` for nested usage.
 *
 * @example
 * const contactForm = form(model, contactFormSchema);
 */
export function contactFormSchema(p: SchemaPathTree<ContactForm>): void {
  required(p.type);
  required(p.first_name);
  noWhitespaceValidator(p.first_name);
  required(p.last_name);
  noWhitespaceValidator(p.last_name);
  required(p.email);
  email(p.email);
  required(p.number);
  phoneNumberValidator(p);
  required(p.country_id);
}

/**
 * Serializes a {@link ContactForm} value into an API-ready payload (Contact
 * without an `id`).
 *
 * Defaults missing fields to their canonical empty values so the backend
 * receives a stable shape.
 *
 * @param formValue The current form value as returned by `form().value()`.
 * @returns A `Contact` object ready to be sent to the API.
 */
export function serializeContactForm(
  formValue: Partial<ContactForm>
): Omit<Contact, 'id'> {
  return {
    type: formValue.type ?? 'admin',
    first_name: formValue.first_name ?? '',
    last_name: formValue.last_name ?? '',
    email: formValue.email ?? '',
    number: formValue.number ?? '',
    has_whatsapp: formValue.has_whatsapp ?? false,
    indutrustMainPoc: formValue.indutrustMainPoc ?? false,
    country_id: formValue.country_id ?? null,
    country_code: formValue.country_code ?? '',
  };
}
